import { DoorsEdit } from "company/detail/components/DoorsEdit";
import { VenueFormState } from "company/detail/components/models";
import { StageEdit } from "company/detail/components/StageEdit";
import { getUpdateVenueInstuction } from "company/detail/utils";
import AgeRestrictionDropdown from "components/dropdowns/reactselect/AgeRestrictionDropdown";
import VenueCategoryDropdown from "components/dropdowns/reactselect/VenueCategoryDropdown";
import { FormSection } from "components/MainSectionTemplate/types";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React from "react";
import { isNullOrEmptyString } from "utilities";
import Doors from "venue/components/Doors";
import Stage from "venue/components/Stage";

export const venueDetailsSection = (company: server.dto.Company, venue: server.dto.VenueDetails, venueFormState: VenueFormState): FormSection => {
  return {
    type: "collapsable",
    title: site.currentEnvironment.functionGroups.venue.singular,
    key: "companyVenueDetails",
    hidden: company.isVenue === "no" || !venue,
    fields: [{
      key: "capacity",
      label: RESX.Venue.Capacity,
      render: () => <div>{venueFormState.capacity} PAX</div>,
      isEmpty: !venueFormState.capacity,
      inPageEdit: {
        initialValue: venueFormState.capacity,
        render: ({ value, onChange, cancel }) => (
          <OptionalNumberInput
            onEnterPressed={(e) => {
              onChange(e, true);
            }}
            onEscPressed={cancel}
            autoFocus
            value={value}
            onChange={onChange}
            minValue={0}
            maxValue={serverReference.constants.venue.maxCapacity}
            canBeEmpty
            decimalPoints={0}
            labelContent="PAX"
            width={100}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: number) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { capacity: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "ageRestriction",
      label: RESX.Venue.AgeRestrictions,
      render: () => <div>{venueFormState.ageRestriction.text}</div>,
      isEmpty: venueFormState.ageRestriction.value === "unknown",
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.ageRestriction,
        render: ({ value, onChange }) => (
          <AgeRestrictionDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.AgeRestriction) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { ageRestriction: value.value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "doors",
      label: RESX.Venue.Doors,
      render: () => <Doors doors={venueFormState.doors} />,
      isEmpty: !venueFormState.doors.open && !venueFormState.doors.close,
      inPageEdit: {
        initialValue: venueFormState.doors,
        render: ({ value, onChange, }) => (
          <DoorsEdit
            value={value}
            onChange={onChange}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.Doors) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, {
            doors: {
              open: value.open?.cultureInvariant,
              close: value.close?.cultureInvariant
            }
          });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "category",
      label: RESX.Venue.Category,
      render: () => <div>{venueFormState.category.text || ""}</div>,
      isEmpty: venueFormState.category.value === "unknown",
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.category,
        render: ({ value, onChange }) => (
          <VenueCategoryDropdown
            value={value}
            onChange={onChange}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: server.dto.VenueCategory) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { category: value.value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "stage",
      label: RESX.Venue.Stage,
      render: () => <Stage stage={venueFormState.stage} />,
      isEmpty: !venueFormState.stage.depth && !venueFormState.stage.width && !venueFormState.stage.toFloor && !venueFormState.stage.toCeiling,
      inPageEdit: {
        initialValue: venueFormState.stage,
        useCustomSavingState: true,
        fullWidth: true,
        render: ({ value, onChange, submit, isSaving }) => (
          <div className="main-section-template-fields-padding">
            <StageEdit
              stage={value}
              onChange={onChange}
              submit={submit}
              isSaving={isSaving}
            />
          </div>
        ),
        type: "custom",
        submitChange: async (value: server.dto.Stage) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { stage: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "vehicleAccess",
      label: RESX.Venue.VehicleAccess,
      render: () => <div>{venueFormState.vehicleAccess}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.vehicleAccess),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.vehicleAccess,
        render: ({ value, onChange, cancel, submit }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxVehicleAccessLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { vehicleAccess: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "parking",
      label: RESX.Venue.Parking,
      render: () => <div>{venueFormState.parking}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.parking),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.parking,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxParkingLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { parking: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }

    }, {
      key: "gearAccess",
      label: RESX.Venue.GearAccess,
      render: () => <div>{venueFormState.gearAccess}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.gearAccess),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.gearAccess,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxGearAccessLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { gearAccess: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "power",
      label: RESX.Venue.Power,
      render: () => <div>{venueFormState.power}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.power),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.power,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxPowerLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { power: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "lighting",
      label: RESX.Venue.Lighting,
      render: () => <div>{venueFormState.lighting}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.lighting),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.lighting,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxLightingLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { lighting: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "dressingRooms",
      label: RESX.Venue.DressingRooms,
      render: () => <div>{venueFormState.dressingRooms}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.dressingRooms),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.dressingRooms,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxDressingRoomsLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { dressingRooms: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }, {
      key: "noiseRestrictions",
      label: RESX.Venue.NoiseRestrictions,
      render: () => <div>{venueFormState.noiseRestrictions}</div>,
      isEmpty: isNullOrEmptyString(venueFormState.noiseRestrictions),
      inPageEdit: {
        fullWidth: true,
        initialValue: venueFormState.noiseRestrictions,
        render: ({ value, onChange, submit, cancel }) => (
          <OptionalTextInput
            focusOnMount
            value={value}
            onChange={onChange}
            maxLength={serverReference.constants.venue.maxNoiseRestrictionsLength}
            onEnter={submit}
            onEsc={cancel}
          />
        ),
        type: "confirmOrCancelButton",
        submitChange: async (value: string) => {
          const instruction = getUpdateVenueInstuction(company.companyId, venueFormState, { noiseRestrictions: value });
          return window.CoreApi.Venue.UpdateDetails(instruction);
        }
      }
    }],
  }
}